'use client';

import React, { Fragment, useEffect } from 'react';
import { Layout, theme } from 'antd';
import styled from '@emotion/styled';
import NextTopLoader from 'nextjs-toploader';
import { WithThemes } from '@/components/ThemesProvider';
import DashboardNavbar from '@/components/Dashboard/Navbar';
import DashboardContent from '@/components/Dashboard/Content';
import { LayoutProps } from '@/utils/interfaces/interfaces';
import { useImageStore, useStore } from '@/store/zustand-solicitud';
import CameraComponent from '@/components/ui/camera';
import { rotateImage } from '@/utils/services/files';
import solicitud from '@/models/solicitud';
import './styles/layout.scss';
import Fondo from '@/icons/fondo-1';
import Fondo2 from '@/icons/fondo-2';
import Image from 'next/image';
import bindLogo from '../../../public/assets/bind-logo.png';

const LayoutChild = styled(Layout)`
  height: calc(var(--vh, 1vh) * 100);
  overflow: hidden;

  // AB#11240: en mobile, con logo + navbar + wizard, el contenido real supera
  // frecuentemente los 100vh -- con overflow:hidden ese excedente queda inaccesible
  // para siempre (a diferencia de auto, que al menos ofrece scroll). Es el techo que
  // bloqueaba que "un solo scroll de pagina" fuera siquiera posible, sin importar como
  // se ajustaran layout-content/overflowContent puertas adentro.
  @media screen and (max-width: 768px) {
    overflow-y: auto;
  }
`;

const PageLoader: React.FC = () => {
  const { token } = theme.useToken();
  return (
    <NextTopLoader
      color={token.colorPrimary}
      initialPosition={1}
      crawlSpeed={200}
      height={3}
      crawl={true}
      showSpinner={false}
      easing="ease"
      speed={500}
      shadow={`0 0 10px ${token.colorPrimary},0 0 5px ${token.colorPrimary}`}
    />
  );
};

const LayoutDashboard: React.FC<LayoutProps> = (props: LayoutProps) => {
  const [isMobile, setIsMobile] = React.useState<boolean>(false);
  const isCameraVisible = useStore((state) => state.camaraVisible);
  const cameraType = useStore((state) => state.cameraType);
  const setScreen = useStore((state) => state.setScreen);
  const navbarVisible = useStore((state) => state.navbarVisible);
  const setIsMobileInStore = useStore((state) => state.setIsMobile);
  const logo = useStore((state) => state.logo);

  // Función para detectar si es móvil
  const checkIsMobile = () => {
    const mobile = window.innerWidth <= 768; // Define el breakpoint para mobile
    setIsMobile(mobile);
    setIsMobileInStore(mobile); // También actualiza el store
  };

  // Detectar si es mobile en el montaje y en cambios de tamaño
  useEffect(() => {
    checkIsMobile(); // Detecta en el primer render
    window.addEventListener('resize', checkIsMobile); // Detecta en cambios de tamaño

    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  useEffect(() => {
    const fixViewport = () => {
      const viewportWidth = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
      document.documentElement.style.setProperty('--vw', `${viewportWidth * 0.01}px`);
    };

    fixViewport();
    window.addEventListener('resize', fixViewport);
    window.addEventListener('orientationchange', fixViewport);

    return () => {
      window.removeEventListener('resize', fixViewport);
      window.removeEventListener('orientationchange', fixViewport);
    };
  }, []);

  // Función para cerrar la cámara
  const handleCloseCamera = () => {
    useStore.setState({ camaraVisible: false, cameraType: null });
    setScreen(solicitud.screens.documento);
  };

  // Capturar imagen y actualizar el estado
  const handleCapture = async (image: string, capture: boolean) => {
    if (cameraType === 'selfie') {
      useImageStore.getState().setSelfieImage(image);
      useStore.setState({ camaraVisible: false, cameraType: null });
      return;
    }

    setScreen(solicitud.screens.documento);
    if (isMobile && capture) {
      image = await rotateImage(image);
    }
    if (cameraType === 'front') {
      useImageStore.getState().setFrontImage(image);
    } else if (cameraType === 'back') {
      useImageStore.getState().setBackImage(image);
    }
    handleCloseCamera();
  };

  return (
    <WithThemes>
      <PageLoader />
      <Layout hasSider className="layout">
        <div className="background-container">{!isMobile ? <Fondo className="svg-background" /> : <Fondo2 className="svg-background" />}</div>

        {(!isCameraVisible || !isMobile) && (
          <LayoutChild className="layout">
            {navbarVisible && (
              <div className="logoLayoutContainer">
                {/* AB#11070: logo puede llegar null/vacio (fetch de entidad todavia no resolvio, o
                    la entidad no tiene logo configurado); next/image explota con src null. */}
                <Image src={logo || bindLogo} alt="Logo" className="logo" width={500} height={500} />
              </div>
            )}
            <DashboardNavbar />
            <DashboardContent>
              <div className="layout-content">{props.children}</div>
            </DashboardContent>
          </LayoutChild>
        )}

        {isMobile && isCameraVisible && (
          <div className="camera-overlay">
            <CameraComponent onClose={handleCloseCamera} onCapture={handleCapture} galeria={true} position={cameraType === 'selfie' ? 'front' : 'back'} />
          </div>
        )}
      </Layout>
    </WithThemes>
  );
};

export default LayoutDashboard;
